/**
 * 공유받은 패스워드 관련 상수
 */

// 공유 상태 enum
export const SHARED_PASSWORD_STATUS = {
  PENDING: 'pending',      // 대기 중
  ACCEPTED: 'accepted',    // 수락됨
  REJECTED: 'rejected',    // 거절됨
  EXPIRED: 'expired'       // 만료됨
};

// 공유 상태 라벨 (한국어)
export const SHARED_PASSWORD_STATUS_LABELS = {
  [SHARED_PASSWORD_STATUS.PENDING]: '대기 중',
  [SHARED_PASSWORD_STATUS.ACCEPTED]: '수락됨',
  [SHARED_PASSWORD_STATUS.REJECTED]: '거절됨',
  [SHARED_PASSWORD_STATUS.EXPIRED]: '만료됨'
};

// 공유 상태 색상 (UI용)
export const SHARED_PASSWORD_STATUS_COLORS = {
  [SHARED_PASSWORD_STATUS.PENDING]: '#f59e0b',    // 주황색
  [SHARED_PASSWORD_STATUS.ACCEPTED]: '#10b981',   // 초록색
  [SHARED_PASSWORD_STATUS.REJECTED]: '#ef4444',   // 빨간색
  [SHARED_PASSWORD_STATUS.EXPIRED]: '#6b7280'     // 회색
};

// 공유 만료 시간 (기본값)
export const SHARED_PASSWORD_EXPIRY = {
  DEFAULT_HOURS: 24,       // 24시간
  MAX_HOURS: 72,          // 최대 72시간
  MIN_HOURS: 1            // 최소 1시간
};

// 공유 알림 타입
export const SHARED_PASSWORD_NOTIFICATION_TYPE = {
  NEW_SHARE: 'new_share',           // 새로운 공유
  ACCEPTED: 'accepted',             // 수락됨
  REJECTED: 'rejected',             // 거절됨
  EXPIRED: 'expired',               // 만료됨
  EXPIRING_SOON: 'expiring_soon'    // 곧 만료
};

// Firestore 컬렉션 이름
export const COLLECTIONS = {
  SHARED_PASSWORDS: 'sharedPasswords',
  SHARED_NOTIFICATIONS: 'sharedNotifications'
};

// 공유 데이터 필드
export const SHARED_PASSWORD_FIELDS = {
  ID: 'id',
  SHARED_BY: 'sharedBy',
  SHARED_TO: 'sharedTo',
  SHARE_TOKEN: 'shareToken',
  PASSWORD_DATA: 'passwordData',
  STATUS: 'status',
  CREATED_AT: 'createdAt',
  EXPIRES_AT: 'expiresAt',
  READ_AT: 'readAt',
  ACCEPTED_AT: 'acceptedAt',
  REJECTED_AT: 'rejectedAt',
  SHARE_MESSAGE: 'shareMessage',
  SENDER_NAME: 'senderName',
  SENDER_EMAIL: 'senderEmail'
};

// 공유 권한 설정
export const SHARED_PASSWORD_PERMISSIONS = {
  VIEW_ONLY: 'view_only',     // 보기만 가능
  COPY_ALLOWED: 'copy_allowed' // 복사 가능
};

// 공유 링크 설정
export const SHARED_LINK_CONFIG = {
  BASE_PATH: '/shared',
  TOKEN_LENGTH: 32,
  SALT_LENGTH: 16
};

// 에러 메시지
export const SHARED_PASSWORD_ERRORS = {
  INVALID_TOKEN: '유효하지 않은 공유 링크입니다.',
  EXPIRED_SHARE: '만료된 공유 링크입니다.',
  UNAUTHORIZED: '이 공유 링크에 접근할 권한이 없습니다.',
  ALREADY_PROCESSED: '이미 처리된 공유 링크입니다.',
  NETWORK_ERROR: '네트워크 오류가 발생했습니다. 다시 시도해주세요.',
  DECRYPTION_ERROR: '데이터 복호화에 실패했습니다.',
  NOT_FOUND: '공유받은 패스워드를 찾을 수 없습니다.'
};

// 성공 메시지
export const SHARED_PASSWORD_SUCCESS = {
  ACCEPTED: '공유받은 패스워드를 수락했습니다.',
  REJECTED: '공유받은 패스워드를 거절했습니다.',
  SAVED: '공유받은 패스워드를 저장했습니다.',
  DELETED: '공유받은 패스워드를 삭제했습니다.'
};

// 공유 데이터 검증 규칙
export const SHARED_PASSWORD_VALIDATION = {
  TOKEN_MIN_LENGTH: 32,
  MESSAGE_MAX_LENGTH: 500,
  TITLE_MAX_LENGTH: 100
};

// 공유 통계 타입
export const SHARED_PASSWORD_STATS = {
  TOTAL_RECEIVED: 'total_received',
  PENDING_COUNT: 'pending_count',
  ACCEPTED_COUNT: 'accepted_count',
  REJECTED_COUNT: 'rejected_count',
  EXPIRED_COUNT: 'expired_count'
};

// 공유 필터 옵션
export const SHARED_PASSWORD_FILTERS = {
  ALL: 'all',
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  UNREAD: 'unread'
};

// 공유 정렬 옵션
export const SHARED_PASSWORD_SORT = {
  CREATED_DESC: 'created_desc',
  CREATED_ASC: 'created_asc',
  EXPIRES_DESC: 'expires_desc',
  EXPIRES_ASC: 'expires_asc',
  STATUS: 'status',
  SENDER: 'sender'
};

// 공유 알림 설정
export const SHARED_PASSWORD_NOTIFICATION_CONFIG = {
  EXPIRY_WARNING_HOURS: 2,    // 만료 2시간 전 알림
  AUTO_CLEANUP_DAYS: 30,      // 30일 후 자동 삭제
  MAX_NOTIFICATIONS: 50       // 최대 알림 개수
};

// 공유 보안 설정
export const SHARED_PASSWORD_SECURITY = {
  MAX_FAILED_ATTEMPTS: 5,     // 최대 실패 시도 횟수
  LOCKOUT_DURATION: 30,       // 잠금 시간 (분)
  REQUIRE_2FA: false,         // 2FA 필수 여부
  LOG_ACCESS: true            // 접근 로그 기록
};

// 공유 UI 설정
export const SHARED_PASSWORD_UI = {
  ITEMS_PER_PAGE: 10,         // 페이지당 아이템 수
  SEARCH_DEBOUNCE: 300,       // 검색 디바운스 시간 (ms)
  REFRESH_INTERVAL: 60000,    // 자동 새로고침 간격 (ms)
  ANIMATION_DURATION: 300     // 애니메이션 지속 시간 (ms)
};

// 공유 데이터 기본값
export const SHARED_PASSWORD_DEFAULTS = {
  STATUS: SHARED_PASSWORD_STATUS.PENDING,
  PERMISSION: SHARED_PASSWORD_PERMISSIONS.VIEW_ONLY,
  EXPIRY_HOURS: SHARED_PASSWORD_EXPIRY.DEFAULT_HOURS,
  SHARE_MESSAGE: '',
  AUTO_ACCEPT: false
};

// 공유 이벤트 타입 (추적용)
export const SHARED_PASSWORD_EVENTS = {
  CREATED: 'shared_password_created',
  VIEWED: 'shared_password_viewed',
  ACCEPTED: 'shared_password_accepted',
  REJECTED: 'shared_password_rejected',
  EXPIRED: 'shared_password_expired',
  DELETED: 'shared_password_deleted'
};

// 공유 메타데이터 키
export const SHARED_PASSWORD_METADATA = {
  SHARE_COUNT: 'share_count',
  LAST_ACCESSED: 'last_accessed',
  ACCESS_COUNT: 'access_count',
  USER_AGENT: 'user_agent',
  IP_ADDRESS: 'ip_address'
};